import { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';

// ── Maintenance Window Configuration ──────────────────────────────
// Set ENABLED to true and update dates for each maintenance window.
// The banner auto-shows from 48 hours before the start until the end time.
const MAINTENANCE_ENABLED = false; // Set to true to activate the banner
const MAINTENANCE_START = new Date('2026-05-02T10:00:00-07:00'); // Sat May 2, 10:00 AM PDT
const MAINTENANCE_END = new Date('2026-05-02T13:00:00-07:00');   // Sat May 2, 1:00 PM PDT
// ──────────────────────────────────────────────────────────────────

export default function MaintenanceBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const check = () => {
      const now = new Date();
      const preNotice = new Date(MAINTENANCE_START.getTime() - 48 * 60 * 60 * 1000); // 48h before
      const active = now >= MAINTENANCE_START && now <= MAINTENANCE_END;
      const shouldShow = MAINTENANCE_ENABLED && now >= preNotice && now <= MAINTENANCE_END;
      setIsActive(active);
      setIsVisible(shouldShow);
    };

    check();
    const interval = setInterval(check, 60_000); // Re-check every minute
    return () => clearInterval(interval);
  }, []);

  const formatLocalTime = (date: Date) => {
    return date.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  if (!isVisible) return null;

  const localStart = formatLocalTime(MAINTENANCE_START);
  const localEnd = formatLocalTime(MAINTENANCE_END);

  return (
    <div className={`relative w-full text-zinc-100 ${isActive ? 'bg-linear-to-r from-red-600 to-red-700' : 'bg-linear-to-r from-orange-500 to-amber-600'}`}>
      <div className="px-4 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-1 items-center justify-center">
            <span className="flex rounded-md bg-white/20 p-1 shrink-0">
              <AlertTriangle className="h-3.5 w-3.5 text-zinc-100" aria-hidden="true" />
            </span>
            <p className="ml-2 text-zinc-100 text-xs">
              <span className="sm:hidden">
                {isActive ? '🔧 Maintenance in progress' : `🔧 Maintenance: ${localStart} - ${localEnd}`}
              </span>
              <span className="hidden sm:inline">
                {isActive ? (
                  <>
                    <span className="font-semibold">Maintenance in Progress</span> — Email sending is temporarily unavailable. Expected completion: {localEnd}.
                  </>
                ) : (
                  <>
                    <span className="font-semibold">Scheduled Maintenance</span> — {localStart} to {localEnd}. Email sending will be unavailable.
                  </>
                )}
              </span>
            </p>
          </div>
          {!isActive && (
            <button
              type="button"
              className="shrink-0 rounded-md p-1 hover:bg-white/10 focus:outline-none"
              onClick={() => setIsVisible(false)}
            >
              <span className="sr-only">Dismiss</span>
              <X className="h-4 w-4 text-zinc-100" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
